import { useState } from "react";
import { hotRanks, type HotGroup } from "@/mocks/daily";

const rankColor = ["text-primary-500", "text-accent-500", "text-secondary-500"];

export default function HotRank() {
  const [active, setActive] = useState<HotGroup>(hotRanks[0]);
  const rising = [...active.items].reverse().slice(0, 3);

  return (
    <section id="hot" className="w-full bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        {/* 分区标题 */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold tracking-widest text-primary-600 uppercase">Trending Now</p>
            <h2 className="mt-2 font-serif text-2xl md:text-4xl font-black text-foreground-950">
              今日实时<span className="text-primary-500">热榜</span>
            </h2>
            <p className="mt-2 text-sm text-foreground-600">全网讨论热度 · 随话题传播滚动更新</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 border border-background-200 px-3 py-1.5 text-xs text-foreground-600">
            <i className="ri-time-line text-accent-600" aria-hidden="true"></i>
            每 15 分钟刷新
          </span>
        </div>

        {/* 榜单切换 */}
        <div className="mt-6 inline-flex flex-wrap gap-1 rounded-full bg-background-50 border border-background-200 p-1">
          {hotRanks.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setActive(g)}
              aria-pressed={active.key === g.key}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                active.key === g.key
                  ? "bg-foreground-950 text-background-50"
                  : "text-foreground-600 hover:bg-background-200"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          {/* 左：主榜单 */}
          <div className="rounded-xl bg-background-50 border border-background-200 p-4 md:p-6">
            <ol className="divide-y divide-background-100">
              {active.items.map((it, i) => (
                <li key={it.title}>
                  <div className="group flex items-start gap-4 py-3.5 md:gap-5">
                    <span
                      className={`font-serif text-2xl md:text-3xl font-black leading-none ${rankColor[i] ?? "text-background-400"}`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-base md:text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {it.title}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-50 px-2 py-0.5 text-secondary-700">
                          {it.tag}
                        </span>
                        {i === 0 && (
                          <span className="inline-flex items-center gap-1 text-primary-600">
                            <i className="ri-fire-fill" aria-hidden="true"></i>爆
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-semibold text-foreground-800">{it.hot.toFixed(1)}</p>
                      <p className="text-[11px] text-foreground-500">万热度</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* 右：上升话题 + 说明 */}
          <div className="flex flex-col gap-5">
            <div className="rounded-xl bg-background-50 border border-background-200 p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-arrow-up-double-line text-accent-600 text-base" aria-hidden="true"></i>
                热度上升最快
              </h3>
              <ul className="mt-3 space-y-3">
                {rising.map((it) => (
                  <li key={it.title} className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <i className="ri-arrow-up-line text-accent-600 text-xs" aria-hidden="true"></i>
                    <span className="min-w-0 flex-1 truncate">{it.title}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl bg-secondary-500 p-5 text-background-50">
              <h3 className="flex items-center gap-2 text-sm font-bold">
                <i className="ri-flag-line text-base" aria-hidden="true"></i>
                热榜怎么来的？
              </h3>
              <p className="mt-2.5 text-sm leading-relaxed text-background-100">
                热度依据全网话题的讨论量、传播速度与持续时间综合计算，仅反映当日讨论趋势，不代表任何立场或倾向。想只挑感兴趣的，往下翻看栏目精选即可。
              </p>
              <a
                href="#featured"
                className="mt-4 inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-background-50 px-3.5 py-2 text-xs font-medium text-secondary-700 transition-colors hover:bg-background-100"
              >
                按栏目查看精选
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}